/**
 * Carga masiva de archivos (versión hardened).
 *
 * Características (tiers 1-4 implementados):
 *  - Pool de workers concurrentes adaptativos (1..6) según degradación
 *  - Retry automático con backoff exponencial por archivo
 *  - Token bucket: limita ops/seg para no saturar Firebase
 *  - Circuit breaker: pausa global si hay errores en cascada
 *  - Detección activa de offline + cancelación inmediata de pendientes
 *  - Persistencia del batch en localStorage + IndexedDB (recovery)
 *  - Validación previa de permisos y límites del batch
 *  - Telemetría a Firebase Analytics (best-effort)
 *  - Virtualización (80 visibles por columna)
 *  - Yield al event loop para no bloquear la UI
 *  - Log colapsable con últimos 500 eventos
 *
 * Notas de diseño:
 *  - Firebase SDK no funciona en Web Workers, así que usamos
 *    yieldToEventLoop() entre operaciones intensivas. La UI nunca se traba.
 *  - Files se persisten en IndexedDB para reanudación tras cerrar el navegador.
 *  - localStorage guarda un resumen ligero del batch (sin Files) para mostrar
 *    el banner de recovery sin tocar IDB.
 */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';

import { upload as uploadAction } from '../../actions/storage/upload';
import { files as filesActions } from '../../actions/database/documents';
import { collectFolderPaths, ensureFolderTree, normalizePath, listExistingFiles } from './bulk_tree';
import { partitionSkipped } from '../../lib/bulk_skip';
import { extractFilesFromDataTransfer } from '../../lib/dnd_traverse';
import { pickDirectory, supportsDirectoryPicker } from '../../lib/dir_picker';
import { useToast } from './toast';
import {
  summarySave, summaryGetAll, summaryRemove,
  idbSaveBatch, idbSaveItems, idbUpdateItemStatus,
  idbDeleteBatch, idbLoadItems, idbPurgeOld,
} from './bulk_storage';
import {
  retryWithBackoff, createTokenBucket, createCircuitBreaker,
  createAdaptiveConcurrency, logAnalyticsEvent, assertCanUploadTo,
  yieldToEventLoop, validateBatchLimits, DEFAULT_LIMITS,
  isPermanentError, attemptTimeoutForSize, orderBySizePhase,
} from './bulk_helpers';
import { logClient } from '../../lib/client_log';
import './bulk_uploader.scss';

// ─── Configuración ──────────────────────────────────────────────────
const RATE_LIMIT_PER_SEC = 6;          // token bucket: ops/seg
// Retry budget: ~5 minutos totales para dar tiempo a que la red se
// recupere antes de marcar un archivo como fallido definitivamente.
// 10 intentos con backoff 2s → 4s → 8s → 16s → 32s → 60s × 4 ≈ 302s
const RETRY_ATTEMPTS = 10;
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 60 * 1000;
const RETRY_FACTOR = 2;
const CIRCUIT_THRESHOLD = 6;           // fallos consecutivos para abrir
const CIRCUIT_WINDOW_MS = 30 * 1000;   // ventana del breaker
const CIRCUIT_COOLDOWN_MS = 30 * 1000; // tiempo de cooldown
const LARGE_FILE_THRESHOLD_BYTES = 25 * 1024 * 1024; // "grande": se sube al final
const UPLOAD_MIN_THROUGHPUT_BPS = 256 * 1024;        // 256 KB/s: piso para el timeout por intento
const UPLOAD_TIMEOUT_MIN_MS = 180 * 1000;            // piso del timeout por intento
const UPLOAD_TIMEOUT_MAX_MS = 600 * 1000;            // tope del timeout por intento
const MAX_CIRCUIT_TRIPS = 3;                         // aperturas del breaker antes de detener el lote
const VISIBLE_LIMIT = 80;              // items renderizados por columna
const LOG_LIMIT = 500;                 // entradas máximas en log
const PERSIST_EVERY = 25;              // persistir cada N items procesados
const YIELD_EVERY = 50;                // yield al event loop cada N items

// Archivos de sistema (Windows / macOS) que se cuelan cuando el usuario
// arrastra carpetas. No tienen valor de negocio y solo ensucian la cola.
// La comparación es case-insensitive porque Windows no distingue mayúsculas.
const SYSTEM_FILE_NAMES = new Set([
  'desktop.ini',
  'thumbs.db',
  '.ds_store',
  '.localized',
  '.spotlight-v100',
  '.trashes',
  'icon\r',          // archivo "Icon" con CR de macOS
  '$recycle.bin',
]);
const isSystemFile = (file) => {
  if (!file?.name) return false;
  return SYSTEM_FILE_NAMES.has(file.name.toLowerCase());
};

// ─── Filtros de exclusión (extensiones / carpetas) ──────────────────
const getExtension = (name) => {
  const idx = String(name || '').lastIndexOf('.');
  if (idx <= 0) return '';
  return name.slice(idx + 1).toLowerCase();
};
// Evalúa una entrada {file, relPath} contra los sets de exclusión.
// Tokens de carpeta: un nombre simple ("backups") excluye cualquier carpeta
// con ese nombre en el camino; un token con "/" ("raiz/2025") excluye esa
// ruta exacta y todo su contenido (así funciona la × del inventario).
const isExcluded = (entry, extSet, dirSet) => {
  const ext = getExtension(entry.file?.name);
  if (ext && extSet.has(ext)) return { excluded: true, rule: 'ext', token: ext };
  if (entry.relPath && dirSet.size > 0) {
    const normSegs = entry.relPath.split('/').map(s => s.trim().normalize('NFC').toLowerCase()).filter(Boolean);
    for (const seg of normSegs) {
      if (dirSet.has(seg)) return { excluded: true, rule: 'dir', token: seg };
    }
    const normPath = normSegs.join('/');
    for (const tok of dirSet) {
      if (tok.includes('/') && (normPath === tok || normPath.startsWith(tok + '/'))) {
        return { excluded: true, rule: 'dir', token: tok };
      }
    }
  }
  return { excluded: false, rule: null, token: null };
};

// ─── Iconos ─────────────────────────────────────────────────────────
const Ico = {
  close: () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 3 L11 11 M11 3 L3 11" /></svg>,
  file: () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"><path d="M3.5 1.5 L9 1.5 L11.5 4 L11.5 12.5 L3.5 12.5 Z" /><path d="M9 1.5 L9 4 L11.5 4" /></svg>,
  folder: () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"><path d="M1.5 4 Q1.5 3 2.5 3 L5 3 L6.5 4.4 L11.5 4.4 Q12.5 4.4 12.5 5.4 L12.5 11 Q12.5 12 11.5 12 L2.5 12 Q1.5 12 1.5 11 Z" /></svg>,
  cloud: () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5.5 14 Q3 14 3 11.5 Q3 9.5 5 9 Q5 6 8 5.5 Q10.5 5.5 11.5 7.5 Q14 7.5 14.5 9.5 Q16.5 10 16.5 12 Q16.5 14 14.5 14 Z" /><path d="M10 9 L10 13.5 M7.5 11.5 L10 9 L12.5 11.5" /></svg>,
  retry: () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 6.5 A 4.5 4.5 0 1 1 3.7 9.5" /><path d="M2 3 L2 6.5 L5.5 6.5" /></svg>,
  trash: () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M2.5 3 L10.5 3 M5 3 L5 2 L8 2 L8 3 M3.5 3 L4 11 L9 11 L9.5 3" /></svg>,
  check: () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 6.8 L5.5 9.8 L10.5 3.5" /></svg>,
  play: () => <svg width="13" height="13" viewBox="0 0 13 13" fill="currentColor"><path d="M3 2 L11 6.5 L3 11 Z" /></svg>,
  pause: () => <svg width="13" height="13" viewBox="0 0 13 13" fill="currentColor"><rect x="3" y="2.5" width="2.5" height="8" rx="0.5" /><rect x="7.5" y="2.5" width="2.5" height="8" rx="0.5" /></svg>,
  queue: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"><path d="M3 4 L13 4 M3 8 L13 8 M3 12 L9 12" /></svg>,
  success: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6.5" /><path d="M5 8.3 L7.2 10.5 L11 6" /></svg>,
  fail: () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6.5" /><path d="M8 4.5 L8 9 M8 11 L8 11.5" /></svg>,
  chevron: (up) => <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform .15s', transform: up ? 'rotate(180deg)' : 'none' }}><path d="M2 4 L5.5 7.5 L9 4" /></svg>,
  offline: () => <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M1 9.5 Q3.5 7 6.5 7 Q9.5 7 12 9.5" /><path d="M3 11.5 Q4.5 10 6.5 10 Q8.5 10 10 11.5" /><circle cx="6.5" cy="12" r="0.5" fill="currentColor" /><path d="M1 1 L12 12" /></svg>,
  warn: () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M7 1 L13 12 L1 12 Z" /><path d="M7 5 L7 9 M7 10.5 L7 11" /></svg>,
};

let _seqId = 0;
const nextId = () => `bu-${++_seqId}-${Date.now().toString(36)}`;
const newBatchId = () => `batch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

function formatBytes(b) {
  if (!b) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function nowHHMMSS() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
}

export function humanizeUploadError(err, { wasOffline = false } = {}) {
  if (wasOffline) return 'Sin conexión a internet. La red no estaba disponible al intentar subir el archivo.';
  if (!err) return 'Error desconocido.';
  if (err?.message === '__TIMEOUT__') return 'La subida se estancó (tiempo de espera agotado). Reintenta.';
  const name = err.name || '';
  const code = err.code || err.serverResponse?.error?.code || '';
  const raw  = (err.message || err.code_ || String(err) || '').toString();
  const msg  = raw.toLowerCase();
  if (name === 'NotReadableError' || msg.includes('could not be read') || msg.includes('notreadableerror') || (msg.includes('permission denied') && msg.includes('file'))) {
    return 'El archivo está abierto en otra aplicación o el SO lo tiene bloqueado. Ciérralo y reintenta.';
  }
  if (name === 'NotFoundError' || (msg.includes('not found') && msg.includes('file'))) {
    return 'El archivo ya no existe en su ubicación original. Vuelve a seleccionarlo.';
  }
  if (code === 'storage/unauthorized' || msg.includes('unauthorized')) return 'Sin permisos para subir a esta carpeta.';
  if (code === 'storage/canceled') return 'Subida cancelada.';
  if (code === 'storage/quota-exceeded') return 'Cuota de almacenamiento excedida.';
  if (code === 'storage/retry-limit-exceeded') return 'Demasiados reintentos fallidos. Verifica tu conexión.';
  if (code === 'storage/invalid-checksum') return 'El archivo se corrompió durante la subida. Reintenta.';
  if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('offline') || code === 'unavailable') {
    return 'Sin conexión o servicio no disponible. Reintenta cuando vuelva la red.';
  }
  if (msg.includes('size 0') || msg.includes('empty')) return 'El archivo está vacío (0 bytes).';
  // Firestore: el documento de la carpeta destino superó 1 MiB. El array
  // files[] de una carpeta crece con cada archivo; al llegar a ~2.000 archivos
  // en la MISMA carpeta, Firestore rechaza toda escritura posterior. Reintentar
  // no ayuda (es determinista) — hay que repartir los archivos en subcarpetas.
  if ((code === 'invalid-argument' || code === 3) &&
      (msg.includes('maximum allowed size') || msg.includes('exceeds the maximum') || msg.includes('1,048,576') || msg.includes('1048576'))) {
    return 'La carpeta destino alcanzó el límite de Firestore (1 MiB por carpeta, ~2.000 archivos). Reparte el resto en subcarpetas.';
  }
  return raw.length > 200 ? raw.substring(0, 200) + '...' : raw;
}

// ─── Drag & drop recursivo ──────────────────────────────────────────
// La exploración del arrastre (API webkitGetAsEntry, reintentos por
// directorio, failedDirs) vive en lib/dnd_traverse.js — testeable y
// resiliente a InvalidStateError con árboles gigantes en unidades
// sincronizadas. Ver ese módulo para el contrato { files, failedDirs }.

// Lista virtualizada simple para el inventario de filtros: se listan TODAS
// las filas (scroll real), pero solo se montan en el DOM las visibles.
// Sin esto, un árbol tipo node_modules (decenas de miles de filas) congela
// el navegador al expandir el panel.
function VirtualRows({ rows, rowHeight = 26, height = 170, renderRow, emptyText }) {
  const [scrollTop, setScrollTop] = useState(0);
  if (!rows.length) {
    return <div style={{ padding: '6px 10px', fontSize: 11.5, opacity: .6, border: '1px solid var(--line, #e8e8e8)', borderRadius: 6 }}>{emptyText}</div>;
  }
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - 5);
  const end = Math.min(rows.length, start + Math.ceil(height / rowHeight) + 10);
  return (
    <div
      style={{ maxHeight: height, overflowY: 'auto', border: '1px solid var(--line, #e8e8e8)', borderRadius: 6 }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      <div style={{ height: rows.length * rowHeight, position: 'relative' }}>
        {rows.slice(start, end).map((r, i) => (
          <div key={r.key || r.tok} style={{ position: 'absolute', top: (start + i) * rowHeight, left: 0, right: 0, height: rowHeight }}>
            {renderRow(r)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Subcomponentes ─────────────────────────────────────────────────
// Botón compacto de fila del inventario de filtros (× apartar / ↩ restaurar).
const inventoryBtnStyle = {
  marginLeft: 'auto', background: 'none', border: '1px solid var(--line, #d9d9d9)',
  borderRadius: 4, cursor: 'pointer', fontSize: 11.5, lineHeight: '16px',
  padding: '0 6px', flexShrink: 0,
};

function FileRow({ item, action }) {
  return (
    <div className={`bu-row bu-row-${item.status}`}>
      <span className="bu-row-ico">{Ico.file()}</span>
      <div className="bu-row-body">
        <div className="bu-row-name" title={item.name}>{item.name}</div>
        <div className="bu-row-meta">
          {formatBytes(item.size)}
          {item.relPath && (
            <span className="bu-row-path" title={item.relPath} style={{ opacity: .7 }}>
              {' · 📁 '}{item.relPath.length > 42 ? `…${item.relPath.slice(-40)}` : item.relPath}
            </span>
          )}
          {item.error && <span className="bu-row-error" title={item.error}> · {item.error}</span>}
        </div>
      </div>
      {action}
    </div>
  );
}

// ─── Componente principal ──────────────────────────────────────────
// canUploadTree: subir con estructura de árbol (rango ≥ 60: contador,
// supervisora, root). unlimited: sin límites de cantidad ni tamaño del lote
// (el personal de nivel global, como siempre). Son permisos distintos.
export function BulkUploader({ open, onClose, targetFolderRef, targetFolderName, onComplete, canUploadTree = false, unlimited = false }) {
  const dispatch = useDispatch();
  const toast = useToast();
  const firebase = useFirebase();

  // Destino RAÍZ del cliente (folders/{cid} es DocumentReference, no
  // CollectionReference). En la raíz no existen archivos sueltos: solo se
  // permite el modo árbol (carpetas completas).
  const isDocTarget = !!targetFolderRef && typeof targetFolderRef.doc !== 'function';

  // Modo árbol (solo root): replicar la estructura de subcarpetas en destino.
  // Carpetas vistas al explorar el origen, INCLUIDAS las vacías. No son
  // archivos, así que no van a la cola; se guardan aparte para poder crear
  // la estructura completa. De los archivos no se puede derivar una carpeta
  // sin archivos, que era justo lo que se perdía.
  const sourceDirsRef = useRef([]);
  const [treeMode, setTreeMode] = useState(false);
  const treeModeRef = useRef(false);
  const setTreeModeBoth = useCallback((v) => { treeModeRef.current = v; setTreeMode(v); }, []);
  // «Incluir las carpetas vacías» (solo con el modo árbol): si está apagado,
  // solo se crean las carpetas que reciben algún archivo.
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const includeEmptyRef = useRef(false);
  const setIncludeEmptyBoth = useCallback((v) => { includeEmptyRef.current = v; setIncludeEmpty(v); }, []);
  // En la raíz el modo árbol es obligatorio: se activa solo al abrir.
  useEffect(() => {
    if (open && isDocTarget && canUploadTree) {
      treeModeRef.current = true;
      setTreeMode(true);
    }
  }, [open, isDocTarget, canUploadTree]);
  // Mapa rutaNormalizada → CollectionReference, construido en la fase de
  // preparación de carpetas (processQueue). null en modo plano.
  const folderMapRef = useRef(null);
  // Reemplazos: itemId → fileDocRef del archivo existente con el mismo nombre
  // en la carpeta destino. El worker lo elimina (va a papelera) antes de subir.
  const replaceMapRef = useRef(new Map());

  // Omitidos: items que ya existían en el destino y el usuario decidió no
  // reemplazar. Salen de la cola antes de arrancar los workers.
  const skippedRef = useRef([]);

  // Filtros de exclusión (todos los usuarios). Modelo reversible: los
  // archivos que coinciden con un filtro se APARTAN de la cola a un pool
  // (excludedPoolRef) y vuelven automáticamente si el filtro se quita.
  const [excludeExts, setExcludeExts] = useState([]);
  const [excludeDirs, setExcludeDirs] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const excludeExtsRef = useRef([]);
  const excludeDirsRef = useRef([]);
  const excludedPoolRef = useRef([]);
  // Versión del contenido de la cola/apartados: el inventario de filtros solo
  // se recalcula cuando esto cambia (no en cada frame de render — con cientos
  // de miles de archivos el recálculo por render congelaba la app).
  const [invRev, setInvRev] = useState(0);
  const bumpInventory = useCallback(() => setInvRev(v => v + 1), []);

  // Source-of-truth: refs.
  const queueRef = useRef([]);
  // Índice del PRÓXIMO item a procesar. Los workers avanzan este puntero en
  // vez de hacer queue.shift(): con 100.000 archivos, shift() reindexaba el
  // array completo por cada archivo (O(n²)) y congelaba la app. Fuera del
  // procesamiento el puntero siempre vuelve a 0 (la cola se compacta).
  const queueHeadRef = useRef(0);
  const succeededRef = useRef([]);
  const failedRef = useRef([]);
  const batchIdRef = useRef(null);
  // Cola restante (lo aún no tomado por los workers).
  const remainingCount = () => queueRef.current.length - queueHeadRef.current;

  const [, forceRender] = useState(0);
  const rerenderScheduled = useRef(false);
  const rerender = useCallback(() => {
    if (rerenderScheduled.current) return;
    rerenderScheduled.current = true;
    requestAnimationFrame(() => {
      rerenderScheduled.current = false;
      forceRender(n => (n + 1) % 1000000);
    });
  }, []);

  // Estado de proceso
  const processingRef = useRef(false);
  const pausedRef = useRef(false);
  const abortRef = useRef(false);
  const [processing, setProcessing] = useState(false);
  const [paused, setPaused] = useState(false);
  const activeWorkRef = useRef(new Map());

  // Conectividad
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const onlineRef = useRef(online);
  useEffect(() => { onlineRef.current = online; }, [online]);

  // Telemetría: contador para enviar evento al cerrar el batch
  const startedAtRef = useRef(0);
  const totalBytesRef = useRef(0);

  // Circuit breaker + token bucket (refs para persistir entre renders)
  const breakerRef = useRef(null);
  const circuitTripsRef = useRef(0);
  const bucketRef = useRef(null);
  const concurrencyRef = useRef(null);
  const [breakerState, setBreakerState] = useState('closed');

  // Inicializa breaker/bucket/concurrencia una sola vez al montar
  useEffect(() => {
    breakerRef.current = createCircuitBreaker({
      failureThreshold: CIRCUIT_THRESHOLD,
      windowMs: CIRCUIT_WINDOW_MS,
      cooldownMs: CIRCUIT_COOLDOWN_MS,
      onStateChange: (s) => {
        setBreakerState(s);
        if (s === 'open') {
          circuitTripsRef.current += 1;
          pushLog('warn', `Circuit breaker ABIERTO (${circuitTripsRef.current}/${MAX_CIRCUIT_TRIPS}): pausando ${CIRCUIT_COOLDOWN_MS / 1000}s tras demasiados errores.`);
          logClient({ event: 'bulk.circuit.open', level: 'warn', message: 'Circuit breaker abierto en carga masiva', data: { tripCount: circuitTripsRef.current, windowMs: CIRCUIT_WINDOW_MS } });
        } else if (s === 'closed') {
          pushLog('info', 'Circuit breaker CERRADO: subidas normalizadas.');
        }
      }
    });
    bucketRef.current = createTokenBucket({ rate: RATE_LIMIT_PER_SEC, capacity: RATE_LIMIT_PER_SEC * 2 });
    concurrencyRef.current = createAdaptiveConcurrency({ initial: 3, min: 1, max: 6 });
    return () => {
      // Limpieza al desmontar
      breakerRef.current = null;
      bucketRef.current = null;
      concurrencyRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log
  const [log, setLog] = useState([]);
  const [logExpanded, setLogExpanded] = useState(false);

  // Diálogo de confirmación propio (reemplaza el window.confirm nativo).
  // askConfirm({...}) devuelve Promise<boolean> con okText/cancelText, o
  // Promise<key> si se pasa buttons: [{ key, label, danger?, primary? }].
  const [confirmDlg, setConfirmDlg] = useState(null);
  const askConfirm = useCallback((opts) => new Promise(resolve => {
    setConfirmDlg({ ...opts, resolve });
  }), []);
  const settleConfirm = (val) => {
    setConfirmDlg(prev => { prev?.resolve?.(val); return null; });
  };

  // Recovery
  const [recoveryCandidates, setRecoveryCandidates] = useState([]);

  // Refs DOM
  const filePickerRef = useRef(null);
  const folderPickerRef = useRef(null);
  const dropRef = useRef(null);
  const logBodyRef = useRef(null);

  // ── Listeners de red ────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const goOnline = () => {
      setOnline(true);
      pushLog('info', 'Conexión restablecida.');
    };
    const goOffline = () => {
      setOnline(false);
      pushLog('warn', 'Conexión perdida. Las subidas pendientes fallarán.');
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Recovery: al abrir, busca batches incompletos ────────────────
  useEffect(() => {
    if (!open) return;
    idbPurgeOld(); // limpia >7d
    const candidates = summaryGetAll().filter(b =>
      b.status === 'processing' || b.status === 'paused' || (b.queueLength > 0)
    );
    setRecoveryCandidates(candidates);
  }, [open]);

  const pushLog = useCallback((level, msg) => {
    setLog(prev => {
      const next = [...prev, { ts: nowHHMMSS(), level, msg }];
      return next.length > LOG_LIMIT ? next.slice(next.length - LOG_LIMIT) : next;
    });
  }, []);

  // ── Persistencia (cada N items o al final) ───────────────────────
  const persistBatch = useCallback(async (extra = {}) => {
    const batchId = batchIdRef.current;
    if (!batchId) return;
    const summary = {
      batchId,
      folderName: targetFolderName || '',
      queueLength: remainingCount(),
      succeededCount: succeededRef.current.length,
      failedCount: failedRef.current.length,
      skippedCount: skippedRef.current.length,
      total: remainingCount() + succeededRef.current.length + failedRef.current.length + skippedRef.current.length,
      treeMode: treeModeRef.current === true,
      includeEmpty: treeModeRef.current === true && includeEmptyRef.current === true,
      ...extra
    };
    summarySave(batchId, summary);
    // IDB: meta + items pendientes (los persistimos para poder reanudar)
    try {
      await idbSaveBatch(batchId, summary);
    } catch { /* silent */ }
  }, [targetFolderName]);

  // ── Filtros: repartición reversible cola ↔ apartados ─────────────
  // Reevalúa TODOS los items (cola + apartados) contra los filtros vigentes.
  // Quitar un filtro restaura automáticamente lo que apartó.
  const repartitionByFilters = useCallback(() => {
    const extSet = new Set(excludeExtsRef.current);
    const dirSet = new Set(excludeDirsRef.current);
    const all = queueRef.current.concat(excludedPoolRef.current);
    const q = [], pool = [];
    for (const it of all) {
      const hit = isExcluded({ file: { name: it.name }, relPath: it.relPath }, extSet, dirSet).excluded;
      (hit ? pool : q).push(it);
    }
    const delta = pool.length - excludedPoolRef.current.length;
    queueRef.current = q;
    excludedPoolRef.current = pool;
    if (delta > 0) pushLog('info', `${delta} archivo(s) apartado(s) de la carga por los filtros.`);
    else if (delta < 0) pushLog('info', `${-delta} archivo(s) restaurado(s) a la cola.`);
    bumpInventory();
    rerender();
    persistBatch();
  }, [pushLog, rerender, persistBatch, bumpInventory]);

  const addExcludeExt = useCallback((tok) => {
    if (excludeExtsRef.current.includes(tok)) return;
    excludeExtsRef.current = [...excludeExtsRef.current, tok];
    setExcludeExts(excludeExtsRef.current);
    repartitionByFilters();
  }, [repartitionByFilters]);
  const removeExcludeExt = useCallback((tok) => {
    excludeExtsRef.current = excludeExtsRef.current.filter(t => t !== tok);
    setExcludeExts(excludeExtsRef.current);
    repartitionByFilters();
  }, [repartitionByFilters]);
  const addExcludeDir = useCallback((tok) => {
    if (excludeDirsRef.current.includes(tok)) return;
    excludeDirsRef.current = [...excludeDirsRef.current, tok];
    setExcludeDirs(excludeDirsRef.current);
    repartitionByFilters();
  }, [repartitionByFilters]);
  const removeExcludeDir = useCallback((tok) => {
    excludeDirsRef.current = excludeDirsRef.current.filter(t => t !== tok);
    setExcludeDirs(excludeDirsRef.current);
    repartitionByFilters();
  }, [repartitionByFilters]);

  // ── Reset completo (al cerrar) ───────────────────────────────────
  const reset = useCallback((opts = { clearPersistence: false }) => {
    abortRef.current = true;
    if (opts.clearPersistence && batchIdRef.current) {
      summaryRemove(batchIdRef.current);
      idbDeleteBatch(batchIdRef.current);
    }
    batchIdRef.current = null;
    queueRef.current = [];
    queueHeadRef.current = 0;
    succeededRef.current = [];
    failedRef.current = [];
    activeWorkRef.current = new Map();
    processingRef.current = false;
    pausedRef.current = false;
    setProcessing(false);
    setPaused(false);
    setLog([]);
    folderMapRef.current = null;
    replaceMapRef.current = new Map();
    skippedRef.current = [];
    treeModeRef.current = false;
    setTreeMode(false);
    includeEmptyRef.current = false;
    setIncludeEmpty(false);
    excludedPoolRef.current = [];
    sourceDirsRef.current = [];
    breakerRef.current?.reset();
    concurrencyRef.current?.reset();
    rerender();
  }, [rerender]);

  // ── Esc cierra ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !processingRef.current) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (logBodyRef.current) logBodyRef.current.scrollTop = 0;
  }, [log]);

  // ── Agregar archivos a la cola (con validación) ───────────────────
  // Contrato: recibe Array<{file, relPath}> (las tres vías de entrada lo
  // producen). relPath = carpeta contenedora relativa; '' si no aplica.
  const addFiles = useCallback(async (entries) => {
    if (!entries?.length) return;
    // Filtra archivos de sistema (desktop.ini, .DS_Store, etc.) antes de
    // validar y agregar — se cuelan cuando el usuario arrastra carpetas
    // de Windows/macOS y no tienen valor para la carga.
    const original = Array.from(entries);
    let filtered = original.filter(e => !isSystemFile(e.file));
    const skipped = original.length - filtered.length;
    if (skipped > 0) {
      pushLog('info', `${skipped} archivo(s) de sistema omitidos (desktop.ini, .DS_Store, Thumbs.db, etc.).`);
    }
    if (filtered.length === 0) return;

    // En la raíz del cliente no se admiten archivos sueltos: solo carpetas
    // completas (modo árbol). Los que no traen ruta se omiten con aviso.
    if (isDocTarget) {
      const loose = filtered.filter(e => !e.relPath);
      if (loose.length > 0) {
        filtered = filtered.filter(e => !!e.relPath);
        pushLog('warn', `${loose.length} archivo(s) suelto(s) omitido(s): en la raíz solo se pueden subir carpetas completas (usa "Seleccionar carpeta" o arrastra una carpeta).`);
        toast.warning('Solo carpetas en la raíz', 'Los archivos sueltos no se pueden subir a la raíz del cliente.');
      }
      if (filtered.length === 0) return;
    }

    // Filtros de exclusión del usuario: lo que coincide se APARTA (pool
    // reversible), no se descarta — el usuario puede restaurarlo quitando
    // el filtro en el inventario.
    const extSet = new Set(excludeExtsRef.current);
    const dirSet = new Set(excludeDirsRef.current);
    const included = [];
    const pooled = [];
    for (const e of filtered) {
      (isExcluded(e, extSet, dirSet).excluded ? pooled : included).push(e);
    }
    if (pooled.length > 0) {
      pushLog('info', `${pooled.length.toLocaleString('es-CO')} archivo(s) apartado(s) por los filtros (restaurables quitando el filtro).`);
    }

    // Validar límites (sobre lo que efectivamente se subirá). El personal de
    // nivel global (`unlimited`) está EXENTO: sube la cantidad y el peso que
    // quiera.
    if (!unlimited) {
      const check = validateBatchLimits(included.map(e => e.file));
      if (!check.ok) {
        toast.error('Lote inválido', check.reason);
        pushLog('error', check.reason);
        return;
      }
      // También considerar lo que ya hay en cola
      const currentTotal = queueRef.current.length + succeededRef.current.length + failedRef.current.length;
      if (currentTotal + included.length > DEFAULT_LIMITS.maxFilesPerBatch) {
        const msg = `Excederías el límite de ${DEFAULT_LIMITS.maxFilesPerBatch.toLocaleString('es-CO')} archivos por lote.`;
        toast.error('Lote demasiado grande', msg);
        pushLog('error', msg);
        return;
      }
    }

    if (!batchIdRef.current) batchIdRef.current = newBatchId();
    const mkItems = async (entries) => {
      const out = [];
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i];
        out.push({
          id: nextId(),
          name: e.file.name,
          size: e.file.size,
          file: e.file,
          relPath: e.relPath || '',
          status: 'queued'
        });
        if (i % YIELD_EVERY === 0) await yieldToEventLoop();
      }
      return out;
    };
    const items = await mkItems(included);
    const pooledItems = await mkItems(pooled);
    queueRef.current = queueRef.current.concat(items);
    excludedPoolRef.current = excludedPoolRef.current.concat(pooledItems);
    if (items.length > 0) pushLog('info', `${items.length.toLocaleString('es-CO')} archivo(s) agregado(s) a la cola.`);
    bumpInventory();
    rerender();
    // Persistir items con sus File objects en IndexedDB (recovery), por
    // TANDAS con yield: una sola transacción de cientos de miles de puts
    // bloqueaba la UI. Fire-and-forget: no retrasa la interacción.
    (async () => {
      const all = items.concat(pooledItems);
      const CHUNK = 2000;
      for (let i = 0; i < all.length; i += CHUNK) {
        try { await idbSaveItems(batchIdRef.current, all.slice(i, i + CHUNK)); } catch { /* recovery best-effort */ }
        await yieldToEventLoop();
      }
    })();
    persistBatch();
  }, [pushLog, rerender, toast, persistBatch, isDocTarget, unlimited, bumpInventory]);

  // ── Drag & drop ─────────────────────────────────────────────────
  useEffect(() => {
    const el = dropRef.current;
    if (!el || !open) return;
    const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); el.classList.add('drag-over'); };
    const onDragLeave = (e) => { e.preventDefault(); el.classList.remove('drag-over'); };
    const onDrop = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.classList.remove('drag-over');
      pushLog('info', 'Explorando lo arrastrado...');
      try {
        // Feedback durante la exploración de carpetas grandes: sin esto,
        // arrastrar un árbol enorme parecía que la app estaba pegada.
        let lastTick = 0;
        const { files, dirs, failedDirs } = await extractFilesFromDataTransfer(e.dataTransfer, (n) => {
          if (n - lastTick >= 2000) {
            lastTick = n;
            pushLog('info', `Explorando... ${n.toLocaleString('es-CO')} archivos encontrados.`);
          }
        });
        if (failedDirs.length) {
          const shown = failedDirs.slice(0, 5).join(', ');
          const more = failedDirs.length > 5 ? ` y ${failedDirs.length - 5} más` : '';
          pushLog('warn',
            `${failedDirs.length} carpeta(s) no se pudieron leer (el contenido cambió durante la exploración): ${shown}${more}. ` +
            `El resto se agregó a la cola. Vuelve a arrastrar solo esas carpetas, o si están en una unidad sincronizada (Google Drive/OneDrive), pausa la sincronización e intenta de nuevo.`);
        }
        // Las carpetas se registran aunque no traigan un solo archivo: es lo
        // que permite replicar las vacías en el destino.
        if (dirs?.length) {
          sourceDirsRef.current = [...sourceDirsRef.current, ...dirs];
          const vacias = dirs.filter(d => !files.some(f => f.relPath === d));
          if (vacias.length) pushLog('info', `${vacias.length} carpeta(s) sin archivos: se crearán si subes la estructura con «Incluir las carpetas vacías».`);
        }
        if (files.length) await addFiles(files);
        else if (dirs?.length) rerender();
        else if (!failedDirs.length) pushLog('warn', 'No se encontraron archivos en lo arrastrado.');
      } catch (err) {
        console.error('drag-drop:', err);
        pushLog('error', `Error procesando arrastre: ${err.message || err}`);
      }
    };
    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('dragleave', onDragLeave);
      el.removeEventListener('drop', onDrop);
    };
  }, [addFiles, pushLog, open, rerender]);

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    addFiles(list.map(f => ({ file: f, relPath: '' })));
  };
  const onPickFolder = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    // webkitRelativePath incluye la carpeta raíz seleccionada — coherente
    // con la ruta que produce el drag & drop de carpetas.
    const entries = list.map(f => ({
      file: f,
      relPath: f.webkitRelativePath ? f.webkitRelativePath.split('/').slice(0, -1).join('/') : ''
    }));
    // Respaldo: <input webkitdirectory> SOLO entrega File. Una carpeta vacía
    // no produce ninguno, así que el navegador ni la reporta y no hay forma de
    // crearla. Se avisa para que el usuario sepa qué hacer.
    if (treeModeRef.current && includeEmptyRef.current) {
      pushLog('warn',
        'Este navegador no permite detectar carpetas vacías desde el botón. ' +
        'Si necesitas incluirlas, arrastra la carpeta al recuadro.');
    }
    addFiles(entries);
  };

  /**
   * Elige una carpeta con la File System Access API, que sí enumera
   * directorios vacíos. Donde no exista (Firefox, Safari) cae al
   * <input webkitdirectory>, que los pierde y lo avisa.
   */
  const onPickFolderButton = async () => {
    if (!supportsDirectoryPicker()) {
      folderPickerRef.current?.click();
      return;
    }
    try {
      pushLog('info', 'Explorando la carpeta seleccionada...');
      let lastTick = 0;
      const res = await pickDirectory((n) => {
        if (n - lastTick >= 2000) {
          lastTick = n;
          pushLog('info', `Explorando... ${n.toLocaleString('es-CO')} archivos encontrados.`);
        }
      });
      if (!res) return; // el usuario cerró el diálogo
      const { files, dirs, failedDirs } = res;
      if (failedDirs.length) {
        pushLog('warn', `${failedDirs.length} carpeta(s) no se pudieron leer: ${failedDirs.slice(0, 5).join(', ')}.`);
      }
      if (dirs?.length) {
        sourceDirsRef.current = [...sourceDirsRef.current, ...dirs];
        const vacias = dirs.filter(d => !files.some(f => f.relPath === d));
        if (vacias.length) pushLog('info', `${vacias.length} carpeta(s) sin archivos: se crearán si subes la estructura con «Incluir las carpetas vacías».`);
      }
      if (files.length) await addFiles(files);
      else if (dirs?.length) rerender();
      else pushLog('warn', 'La carpeta seleccionada está vacía.');
    } catch (err) {
      console.error('pickDirectory:', err);
      pushLog('error', `No se pudo leer la carpeta: ${err.message || err}`);
    }
  };

  // ── Procesamiento ───────────────────────────────────────────────
  const processQueue = useCallback(async (retryFromFailed = false) => {
    if (processingRef.current) return;
    if (!targetFolderRef) {
      toast.error('No hay carpeta destino', 'Selecciona una carpeta donde subir los archivos.');
      pushLog('error', 'No hay carpeta destino definida.');
      return;
    }
    // En la raíz del cliente solo se permite el modo árbol (no hay dónde
    // poner archivos sueltos). El checkbox se fuerza al abrir; esto es un
    // cinturón de seguridad.
    if (isDocTarget && !(canUploadTree && treeModeRef.current)) {
      toast.error('Solo carpetas en la raíz', 'En la raíz del cliente la carga masiva requiere subir la estructura de árbol (contador, supervisora o root).');
      pushLog('error', 'Intento de carga plana en la raíz del cliente — bloqueado.');
      return;
    }

    // Validación previa de permisos sobre el destino
    try {
      await assertCanUploadTo(targetFolderRef);
    } catch (e) {
      toast.error('No se puede subir aquí', e.message);
      pushLog('error', `Validación previa falló: ${e.message}`);
      return;
    }

    if (retryFromFailed && failedRef.current.length) {
      const retriable = failedRef.current.filter(f => !f.permanent);
      const permanentKept = failedRef.current.filter(f => f.permanent);
      if (retriable.length) {
        const retried = retriable.map(f => ({ ...f, status: 'queued', error: undefined }));
        queueRef.current = retried.concat(queueRef.current);
      }
      failedRef.current = permanentKept;
      if (permanentKept.length) {
        pushLog('warn', `${permanentKept.length} archivo(s) con error permanente NO se reintentan (revisa la razón).`);
      }
      rerender();
    }

    if (queueRef.current.length === 0) {
      toast.info('No hay archivos en la cola para subir.');
      return;
    }

    // Dos fases: primero los pequeños (progreso rápido), los grandes al final
    // (se suben con timeout amplio). queueHeadRef es 0 al iniciar el lote.
    queueRef.current = orderBySizePhase(queueRef.current, LARGE_FILE_THRESHOLD_BYTES);

    // Setup
    processingRef.current = true;
    circuitTripsRef.current = 0;
    setProcessing(true);
    abortRef.current = false;
    pausedRef.current = false;
    setPaused(false);
    startedAtRef.current = Date.now();
    totalBytesRef.current = queueRef.current.reduce((s, i) => s + (i.size || 0), 0);
    breakerRef.current?.reset();

    if (!batchIdRef.current) batchIdRef.current = newBatchId();
    await persistBatch({ status: 'processing', startedAt: Date.now() });

    // ── Fase de preparación de carpetas (modo árbol, solo root) ─────
    // Crea/fusiona la estructura de carpetas en el destino ANTES de subir.
    // Idempotente: si falla o se reanuda, re-ejecutar encuentra lo creado.
    folderMapRef.current = null;
    if (canUploadTree && treeModeRef.current) {
      try {
        pushLog('info', 'Preparando estructura de carpetas en el destino...');
        // Con «Incluir las carpetas vacías», las carpetas vistas en el origen
        // entran aunque no tengan archivos; se les aplican las mismas
        // exclusiones que a los archivos. Sin la casilla, solo se crean las
        // carpetas que reciben algún archivo.
        const extSet = new Set(excludeExtsRef.current);
        const dirSet = new Set(excludeDirsRef.current);
        const extraDirs = (includeEmptyRef.current ? sourceDirsRef.current : []).filter(
          d => !isExcluded({ file: { name: '' }, relPath: d }, extSet, dirSet).excluded
        );
        const dirPaths = collectFolderPaths(queueRef.current, extraDirs);
        if (dirPaths.length > 0) {
          const { map, created, merged } = await ensureFolderTree({
            targetFolderRef,
            dirPaths,
            dispatch,
            firebase,
            onProgress: (level, msg) => pushLog(level, msg),
          });
          folderMapRef.current = map;
          // Items cuya carpeta no quedó en el mapa (referencia irresoluble,
          // etc.) van a fallidos con mensaje claro antes de subir nada.
          const unmapped = queueRef.current.filter(it => it.relPath && !map.has(normalizePath(it.relPath)));
          if (unmapped.length > 0) {
            queueRef.current = queueRef.current.filter(it => !it.relPath || map.has(normalizePath(it.relPath)));
            for (const it of unmapped) {
              const msg = `No se pudo preparar la carpeta destino "${it.relPath}".`;
              failedRef.current.push({ ...it, status: 'failed', error: msg });
              await idbUpdateItemStatus(it.id, { status: 'failed', error: msg });
            }
            pushLog('error', `${unmapped.length} archivo(s) movido(s) a fallidos: su carpeta destino no se pudo preparar.`);
            rerender();
          }
          pushLog('info', `Estructura lista: ${dirPaths.length} carpeta(s) (${created} creada(s), ${merged} fusionada(s)).`);
        } else {
          pushLog('info', 'Los archivos en cola no traen subcarpetas: se subirán directo al destino.');
        }

        // ── Detección de archivos existentes (reemplazo con aviso) ───
        // Compara los nombres en cola contra los archivos que ya existen en
        // cada carpeta destino. Si hay coincidencias, avisa al usuario: al
        // continuar, los existentes se reemplazan (el anterior va a papelera).
        replaceMapRef.current = new Map();
        pushLog('info', 'Verificando archivos existentes en el destino...');
        const byDest = new Map(); // destKey -> items[]
        for (const it of queueRef.current) {
          const key = it.relPath ? normalizePath(it.relPath) : '';
          if (!byDest.has(key)) byDest.set(key, []);
          byDest.get(key).push(it);
        }
        for (const [key, destItems] of byDest.entries()) {
          const destRef = key ? folderMapRef.current?.get(key) : (!isDocTarget ? targetFolderRef : null);
          if (!destRef) continue;
          const existing = await listExistingFiles(destRef);
          if (!existing.length) continue;
          const byName = new Map(existing.map(f => [f.name.trim().normalize('NFC').toLowerCase(), f.fileDocRef]));
          for (const it of destItems) {
            const hit = byName.get(it.name.trim().normalize('NFC').toLowerCase());
            if (hit) replaceMapRef.current.set(it.id, hit);
          }
        }
        if (replaceMapRef.current.size > 0) {
          const n = replaceMapRef.current.size;
          const choice = await askConfirm({
            title: 'Archivos ya existentes',
            message: `${n.toLocaleString('es-CO')} archivo(s) ya existen en las carpetas de destino con el mismo nombre.\n\n` +
              `• Reemplazar: los existentes se reemplazan por los nuevos; los anteriores quedan en la papelera y podrás recuperarlos desde allí.\n` +
              `• Omitir: se suben solo los archivos nuevos; los existentes quedan intactos.`,
            danger: true,
            buttons: [
              { key: 'cancel', label: 'Cancelar' },
              { key: 'skip', label: 'Omitir existentes' },
              { key: 'replace', label: 'Reemplazar existentes', danger: true },
            ],
          });
          if (choice === 'cancel') {
            pushLog('warn', `Carga cancelada por el usuario: ${n} archivo(s) serían reemplazados.`);
            replaceMapRef.current = new Map();
            processingRef.current = false;
            setProcessing(false);
            await persistBatch({ status: 'paused' });
            return;
          }
          if (choice === 'skip') {
            const { pending, skipped } = partitionSkipped(queueRef.current, replaceMapRef.current);
            queueRef.current = pending;
            queueHeadRef.current = 0;
            skippedRef.current = skippedRef.current.concat(skipped);
            replaceMapRef.current = new Map();
            pushLog('info', `${skipped.length.toLocaleString('es-CO')} archivo(s) ya existían y se omitieron; se subirán solo los nuevos.`);
            rerender();
            // Persistir el estado para que un recovery no los resucite como
            // pendientes. Por TANDAS (mismo patrón de addFiles): con decenas
            // de miles de duplicados, un put por item tardaba minutos.
            // Nota: si el guardado fire-and-forget de addFiles aún corre, un
            // item podría quedar 'queued' en IDB; es auto-corregible — la
            // re-detección re-ofrece el diálogo al reanudar.
            const CHUNK = 2000;
            for (let i = 0; i < skipped.length; i += CHUNK) {
              try { await idbSaveItems(batchIdRef.current, skipped.slice(i, i + CHUNK)); } catch { /* recovery best-effort */ }
              await yieldToEventLoop();
            }
          } else if (choice === 'replace') {
            pushLog('warn', `${n.toLocaleString('es-CO')} archivo(s) existentes serán reemplazados (los anteriores van a la papelera).`);
          } else {
            // Defensivo: cualquier resultado inesperado del diálogo se trata
            // como cancelar — la ruta destructiva debe ser explícita.
            pushLog('warn', 'Respuesta inesperada del diálogo: la carga se pausa por seguridad.');
            replaceMapRef.current = new Map();
            processingRef.current = false;
            setProcessing(false);
            await persistBatch({ status: 'paused' });
            return;
          }
        } else {
          pushLog('info', 'No hay archivos duplicados en el destino.');
        }
      } catch (e) {
        const msg = e?.message || 'Error desconocido preparando las carpetas.';
        toast.error('No se pudo preparar la estructura de carpetas', msg);
        pushLog('error', `Fase de carpetas falló: ${msg}`);
        processingRef.current = false;
        setProcessing(false);
        await persistBatch({ status: 'paused' });
        return;
      }
      if (queueRef.current.length === 0) {
        if (skippedRef.current.length > 0) {
          pushLog('info', 'No hay archivos nuevos para subir: todos ya existían en el destino.');
          await persistBatch({ status: 'completed', completedAt: Date.now() });
        }
        processingRef.current = false;
        setProcessing(false);
        rerender();
        return;
      }
    }

    pushLog('info', `Iniciando subida de ${queueRef.current.length.toLocaleString('es-CO')} archivo(s) (concurrencia adaptativa, rate ${RATE_LIMIT_PER_SEC}/s).`);

    let processedCount = 0;
    let activeWorkers = 0;

    // Worker que procesa items
    const worker = async (workerId) => {
      activeWorkers++;
      while (true) {
        if (abortRef.current) break;
        // Pausa cooperativa
        while (pausedRef.current && !abortRef.current) {
          await new Promise(r => setTimeout(r, 200));
        }
        if (abortRef.current) break;

        // Circuit breaker: si está abierto, espera; si ya se abrió demasiadas
        // veces en este lote, detener (evita ciclado infinito).
        if (!breakerRef.current?.canPass()) {
          if (circuitTripsRef.current >= MAX_CIRCUIT_TRIPS) {
            if (!abortRef.current) {
              abortRef.current = true;
              const remaining = Math.max(0, queueRef.current.length - queueHeadRef.current);
              pushLog('error', `Carga detenida: el circuito se abrió ${circuitTripsRef.current} veces por errores repetidos. Revisa los fallidos y reintenta.`);
              logClient({ event: 'bulk.batch.halted', level: 'error', message: 'Carga masiva detenida por aperturas repetidas del breaker', data: { reason: 'circuit_tripped', tripCount: circuitTripsRef.current, uploaded: succeededRef.current.length, failed: failedRef.current.length, remaining } });
            }
            break;
          }
          await new Promise(r => setTimeout(r, 1000));
          continue;
        }

        // Offline → marcar TODA la cola pendiente como fallida
        if (!onlineRef.current) {
          await new Promise(r => setTimeout(r, 500));
          if (!onlineRef.current) {
            while (queueHeadRef.current < queueRef.current.length && !abortRef.current) {
              const it = queueRef.current[queueHeadRef.current++];
              failedRef.current.push({
                ...it,
                status: 'failed',
                error: humanizeUploadError(null, { wasOffline: true })
              });
              processedCount++;
              await idbUpdateItemStatus(it.id, { status: 'failed', error: 'offline' });
            }
            rerender();
            break;
          }
        }

        // Concurrencia adaptativa: si excedemos el actual, este worker sale
        const cur = concurrencyRef.current?.getCurrent() || 3;
        if (activeWorkers > cur) {
          activeWorkers--;
          break;
        }

        // Toma el siguiente (puntero O(1), sin reindexar el array)
        const item = queueHeadRef.current < queueRef.current.length
          ? queueRef.current[queueHeadRef.current++]
          : undefined;
        if (!item) break;

        activeWorkRef.current.set(workerId, item.name);
        rerender();

        const idx = processedCount + 1;
        const startTime = Date.now();
        pushLog('info', `[W${workerId}#${idx}] Subiendo "${item.name}" (${formatBytes(item.size)})`);

        // Destino por item: en modo árbol cada archivo va a la colección de
        // su subcarpeta (mapa de la fase de preparación); en plano, al destino.
        const destRef = (treeModeRef.current && item.relPath && folderMapRef.current)
          ? folderMapRef.current.get(normalizePath(item.relPath))
          : targetFolderRef;

        try {
          if (treeModeRef.current && item.relPath && !destRef) {
            // Defensivo: item reintentado sin fase de mapeo previa.
            throw new Error(`No se pudo preparar la carpeta destino "${item.relPath}". Usa "Iniciar carga" para reconstruir la estructura.`);
          }
          // Reemplazo: si ya existe un archivo con el mismo nombre en el
          // destino (detectado y confirmado en la fase previa), se elimina
          // primero (queda en papelera) y luego se sube el nuevo.
          const replaceRef = replaceMapRef.current.get(item.id);
          if (replaceRef) {
            pushLog('warn', `[W${workerId}#${idx}] Reemplazando "${item.name}" (el anterior va a la papelera).`);
            try {
              await dispatch(filesActions.delete({ roles: { delete: true }, reference: replaceRef }));
            } catch (e) {
              // Si el existente ya no está (borrado por otro usuario), seguimos.
              pushLog('warn', `[W${workerId}#${idx}] No se pudo eliminar el existente (${e?.message || 'ya no existe'}); se sube el nuevo de todas formas.`);
            }
            replaceMapRef.current.delete(item.id);
          }
          // Token bucket: respeta el rate limit
          await bucketRef.current?.take();

          // Subida con retry interno (sólo errores transitorios).
          // Total de esperas entre intentos ≈ 5 min — suficiente para que
          // una conexión inestable se recupere antes de marcar como fallido.
          // El offlinePassedRef permite cortar el sleep entre reintentos si
          // la red vuelve mientras esperamos.
          let sawOffline = false;
          await retryWithBackoff(
            async (attempt) => {
              if (attempt > 1) pushLog('info', `[W${workerId}#${idx}] Intento ${attempt}/${RETRY_ATTEMPTS}`);
              const uploadPromise = dispatch(uploadAction(item.file, destRef || targetFolderRef));
              // Race con detección de offline
              let timeoutId;
              const attemptTimeout = attemptTimeoutForSize(item.size, {
                minMs: UPLOAD_TIMEOUT_MIN_MS, maxMs: UPLOAD_TIMEOUT_MAX_MS, throughputBps: UPLOAD_MIN_THROUGHPUT_BPS,
              });
              await Promise.race([
                uploadPromise,
                new Promise((_, reject) => {
                  const checker = setInterval(() => {
                    if (!onlineRef.current) { clearInterval(checker); reject(new Error('__OFFLINE__')); }
                    if (abortRef.current) { clearInterval(checker); reject(new Error('__ABORTED__')); }
                  }, 1000);
                  uploadPromise.finally(() => clearInterval(checker));
                }),
                new Promise((_, reject) => {
                  timeoutId = setTimeout(() => reject(new Error('__TIMEOUT__')), attemptTimeout);
                  uploadPromise.finally(() => clearTimeout(timeoutId));
                }),
              ]);
            },
            {
              attempts: RETRY_ATTEMPTS,
              baseDelay: RETRY_BASE_DELAY_MS,
              factor: RETRY_FACTOR,
              maxDelay: RETRY_MAX_DELAY_MS,
              isAbort: () => abortRef.current,
              // Importante: NO pasamos isOffline aquí — queremos QUE reintente
              // aún si la red está caída, porque puede volver durante el sleep.
              // El __OFFLINE__ se propagará del upload mismo cuando llegue.
              onWaiting: (delayMs, nextAttempt) => {
                const sec = Math.round(delayMs / 1000);
                pushLog('warn', `[W${workerId}#${idx}] Esperando ${sec}s antes del intento ${nextAttempt}/${RETRY_ATTEMPTS}...`);
              },
              // Si la red estaba caída y vuelve durante el sleep, corta la espera
              // para reintentar de inmediato.
              onRetryWake: () => {
                if (!onlineRef.current) { sawOffline = true; return false; }
                if (sawOffline && onlineRef.current) {
                  sawOffline = false;
                  pushLog('info', `[W${workerId}#${idx}] Red restablecida durante el sleep — reintentando inmediatamente.`);
                  return true;
                }
                return false;
              },
            }
          );

          // Éxito
          const duration = Date.now() - startTime;
          concurrencyRef.current?.recordDuration(duration);
          breakerRef.current?.recordSuccess();
          succeededRef.current.push({ ...item, status: 'success' });
          await idbUpdateItemStatus(item.id, { status: 'success' });
          pushLog('success', `[W${workerId}#${idx}] OK "${item.name}" en ${(duration / 1000).toFixed(1)}s`);
        } catch (err) {
          const isOfflineErr = err?.message === '__OFFLINE__' || !onlineRef.current;
          const isAborted = err?.message === '__ABORTED__';
          if (isAborted) break;
          const msg = humanizeUploadError(err, { wasOffline: isOfflineErr });
          const permanent = isPermanentError(err);
          failedRef.current.push({ ...item, status: 'failed', error: msg, permanent });
          await idbUpdateItemStatus(item.id, { status: 'failed', error: msg, permanent });
          // Solo los errores TRANSITORIOS alimentan el circuit breaker. Un error
          // permanente (p.ej. carpeta llena → invalid-argument por superar 1 MiB)
          // es determinista y localizado a esa carpeta: no debe abrir el circuito
          // ni detener el lote completo, porque las DEMÁS carpetas sí pueden
          // seguir recibiendo archivos. Antes, una sola carpeta llena tumbaba
          // toda la carga (6 fallos → breaker, 3 aperturas → abort del lote).
          if (!permanent) breakerRef.current?.recordFailure();
          pushLog('error', `[W${workerId}#${idx}] Falló "${item.name}": ${msg}${permanent ? ' (no se reintentará)' : ''}`);
          if (permanent) {
            // Mensaje completo (antes se cortaba a 300 chars y la ruta del doc
            // consumía todo el presupuesto, ocultando la CAUSA del rechazo —
            // p.ej. "...exceeds the maximum allowed size of 1,048,576 bytes").
            logClient({ event: 'bulk.upload.permanent_error', level: 'error', message: `Fallo permanente subiendo "${item.name}"`, data: { fileName: item.name, code: err?.code ?? null, rawMessage: (err?.message || '').slice(0, 1500), folderPath: item.relPath || null } });
          }
        } finally {
          activeWorkRef.current.delete(workerId);
          processedCount++;
          // Persistir cada N items
          if (processedCount % PERSIST_EVERY === 0) {
            await persistBatch();
          }
          // Yield al event loop para que la UI no se trabe
          if (processedCount % YIELD_EVERY === 0) await yieldToEventLoop();
          rerender();
        }
      }
      activeWorkers--;
    };

    // Lanza hasta MAX workers (la concurrencia adaptativa cierra los excedentes)
    const MAX_WORKERS = 6;
    const workers = [];
    const initialCount = Math.min(MAX_WORKERS, concurrencyRef.current?.getCurrent() || 3, queueRef.current.length);
    for (let i = 1; i <= initialCount; i++) workers.push(worker(i));
    await Promise.all(workers);

    // Compactar la cola: descartar lo ya tomado por los workers y resetear
    // el puntero (los items no procesados por pausa/cancelación se conservan).
    if (queueHeadRef.current > 0) {
      queueRef.current = queueRef.current.slice(queueHeadRef.current);
      queueHeadRef.current = 0;
    }

    processingRef.current = false;
    setProcessing(false);
    activeWorkRef.current.clear();

    const elapsed = Date.now() - startedAtRef.current;
    const succCount = succeededRef.current.length;
    const failCount = failedRef.current.length;
    const skipCount = skippedRef.current.length;

    pushLog('info', `Cola procesada. Total: ${processedCount.toLocaleString('es-CO')} (✓ ${succCount} / ✗ ${failCount}${skipCount > 0 ? ` / ⊘ ${skipCount} omitido(s)` : ''}) en ${(elapsed / 1000).toFixed(1)}s`);
    await persistBatch({ status: 'completed', completedAt: Date.now() });

    // Telemetría agregada
    logAnalyticsEvent('bulk_upload_completed', {
      total: processedCount,
      succeeded: succCount,
      failed: failCount,
      skipped: skippedRef.current.length,
      duration_ms: elapsed,
      avg_size_bytes: processedCount > 0 ? Math.round(totalBytesRef.current / processedCount) : 0,
      had_offline: !onlineRef.current ? 1 : 0,
      circuit_opened: breakerRef.current?.getState() === 'open' ? 1 : 0,
    });

    logClient({
      event: 'bulk.batch.summary',
      level: failCount > 0 ? 'warn' : 'info',
      message: 'Resumen de carga masiva',
      data: {
        total: processedCount,
        uploaded: succCount,
        failed: failCount,
        permanentCount: failedRef.current.filter(f => f.permanent).length,
        skipped: skipCount,
        durationMs: elapsed,
        halted: !!abortRef.current,
      },
    });

    rerender();
    if (onComplete) {
      try { onComplete(); } catch (e) { console.warn('onComplete err:', e); }
    }
  }, [targetFolderRef, dispatch, pushLog, onComplete, toast, rerender, persistBatch, canUploadTree, firebase]);

  // ── Acciones del usuario ────────────────────────────────────────
  const togglePause = () => {
    const next = !pausedRef.current;
    pausedRef.current = next;
    setPaused(next);
    pushLog(next ? 'warn' : 'info', next ? 'Subida pausada.' : 'Subida reanudada.');
    persistBatch({ status: next ? 'paused' : 'processing' });
  };

  const cancelAll = () => {
    abortRef.current = true;
    pausedRef.current = false;
    setPaused(false);
    pushLog('warn', 'Cancelando subida...');
  };

  const clearQueue = () => { queueRef.current = []; queueHeadRef.current = 0; excludedPoolRef.current = []; pushLog('info', 'Cola limpiada.'); bumpInventory(); rerender(); persistBatch(); };
  const removeFromQueue = (id) => { queueRef.current = queueRef.current.filter(x => x.id !== id); bumpInventory(); rerender(); };
  const retryOne = (id) => {
    const item = failedRef.current.find(x => x.id === id);
    if (!item) return;
    if (item.permanent) {
      toast.warning('No se puede reintentar', `"${item.name}" falló por un error permanente: ${item.error}`);
      pushLog('warn', `"${item.name}" no se reintenta (error permanente).`);
      return;
    }
    failedRef.current = failedRef.current.filter(x => x.id !== id);
    queueRef.current = queueRef.current.concat([{ ...item, status: 'queued', error: undefined }]);
    pushLog('info', `"${item.name}" devuelto a la cola.`);
    bumpInventory();
    rerender();
  };
  const removeFromFailed = (id) => { failedRef.current = failedRef.current.filter(x => x.id !== id); rerender(); };
  const clearSucceeded = () => { succeededRef.current = []; rerender(); };

  // ── Recovery: reanudar batch desde IDB ──────────────────────────
  const resumeBatch = async (batchId) => {
    try {
      const items = await idbLoadItems(batchId);
      if (!items.length) {
        toast.warning('Sin archivos para reanudar', 'El registro del batch existe pero no hay items persistidos.');
        summaryRemove(batchId);
        idbDeleteBatch(batchId);
        setRecoveryCandidates(prev => prev.filter(c => c.batchId !== batchId));
        return;
      }
      // Restaurar el estado
      batchIdRef.current = batchId;
      queueHeadRef.current = 0;
      queueRef.current = items.filter(it => it.status === 'queued' || it.status === 'retrying').map(it => ({
        id: it.id, name: it.name, size: it.size, file: it.file, relPath: it.relPath || '', status: 'queued'
      }));
      succeededRef.current = items.filter(it => it.status === 'success').map(it => ({
        id: it.id, name: it.name, size: it.size, relPath: it.relPath || '', status: 'success'
      }));
      failedRef.current = items.filter(it => it.status === 'failed').map(it => ({
        id: it.id, name: it.name, size: it.size, file: it.file, relPath: it.relPath || '', status: 'failed', error: it.error, permanent: !!it.permanent
      }));
      skippedRef.current = items.filter(it => it.status === 'skipped').map(it => ({
        id: it.id, name: it.name, size: it.size, relPath: it.relPath || '', status: 'skipped'
      }));
      // Restaurar el modo árbol si el batch se creó con él. El mapa de
      // carpetas NO se persiste: se reconstruye al pulsar Iniciar (la fase
      // de preparación es idempotente gracias a la fusión por nombre).
      const cand = recoveryCandidates.find(c => c.batchId === batchId);
      if (cand?.treeMode) {
        if (canUploadTree) {
          setTreeModeBoth(true);
          setIncludeEmptyBoth(cand.includeEmpty === true);
          pushLog('info', 'Este lote se creó en modo árbol: la estructura de carpetas se replicará al reanudar.');
        } else {
          pushLog('warn', 'Este lote se creó en modo árbol, pero tu usuario no tiene esa opción: se reanudará en modo plano.');
        }
      }
      setRecoveryCandidates([]);
      pushLog('info', `Reanudando batch ${batchId}: ${queueRef.current.length} pendientes, ${succeededRef.current.length} OK, ${failedRef.current.length} fallidos.`);
      bumpInventory();
      rerender();
    } catch (e) {
      console.error('resumeBatch:', e);
      toast.error('No se pudo reanudar', e.message || '');
    }
  };

  const discardRecovery = (batchId) => {
    summaryRemove(batchId);
    idbDeleteBatch(batchId);
    setRecoveryCandidates(prev => prev.filter(c => c.batchId !== batchId));
  };

  const handleClose = async () => {
    if (processingRef.current) {
      const ok = await askConfirm({
        title: 'Subida en proceso',
        message: 'Hay una subida en proceso. ¿Deseas cerrar y cancelarla? Podrás reanudarla después desde este mismo panel.',
        okText: 'Cerrar y cancelar',
        cancelText: 'Seguir subiendo',
        danger: true,
      });
      if (!ok) return;
      abortRef.current = true;
      // Persistir el estado actual antes de cerrar
      persistBatch({ status: 'paused', closedByUser: true });
    } else {
      // Si está completado, limpiar la persistencia
      if ((succeededRef.current.length > 0 || skippedRef.current.length > 0) && queueRef.current.length === 0 && failedRef.current.length === 0) {
        reset({ clearPersistence: true });
      } else {
        reset();
      }
    }
    onClose?.();
  };

  const cleanFolderName = useMemo(() => {
    if (!targetFolderName) return '';
    return targetFolderName.includes(' — ')
      ? targetFolderName.substring(targetFolderName.indexOf(' — ') + 3)
      : targetFolderName;
  }, [targetFolderName]);

  // ── Inventario para el panel de filtros: TODO lo detectado (cola +
  // apartados) como dos listas — tipos de archivo y árbol de carpetas.
  // MEMOIZADO y solo si el panel está expandido: recalcularlo en cada render
  // con cientos de miles de archivos congelaba la app.
  const { extRows, dirRows } = useMemo(() => {
    if (!filtersExpanded) return { extRows: [], dirRows: [] };
    const extSetView = new Set(excludeExts);
    const dirSetView = new Set(excludeDirs);
    const inventoryItems = queueRef.current.concat(excludedPoolRef.current);

    const extRowsMap = new Map(); // ext -> count
    const dirRowsMap = new Map(); // rutaNormalizada -> {key, name, depth, count}
    for (const it of inventoryItems) {
      const ext = getExtension(it.name);
      if (ext) extRowsMap.set(ext, (extRowsMap.get(ext) || 0) + 1);
      if (it.relPath) {
        const segs = it.relPath.split('/').filter(Boolean);
        let norm = '';
        for (let i = 0; i < segs.length; i++) {
          const nseg = segs[i].trim().normalize('NFC').toLowerCase();
          norm = norm ? `${norm}/${nseg}` : nseg;
          const row = dirRowsMap.get(norm) || { key: norm, name: segs[i], depth: i, count: 0 };
          row.count++;
          dirRowsMap.set(norm, row);
        }
      }
    }
    const extRows = Array.from(extRowsMap.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([tok, count]) => ({ tok, count, excluded: extSetView.has(tok) }));
    // Un dir queda excluido si su ruta (o un ancestro) tiene token de ruta, o
    // si algún segmento coincide con un token simple. "direct" = su propio token
    // exacto (solo ese muestra ↩ para restaurar).
    const isDirRowExcluded = (normPath) => {
      for (const seg of normPath.split('/')) if (dirSetView.has(seg)) return true;
      for (const tok of dirSetView) {
        if (tok.includes('/') && (normPath === tok || normPath.startsWith(tok + '/'))) return true;
      }
      return false;
    };
    const dirRows = Array.from(dirRowsMap.values())
      .sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0)
      .map(r => ({ ...r, excluded: isDirRowExcluded(r.key), direct: dirSetView.has(r.key) }));
    return { extRows, dirRows };
    // invRev representa el contenido de queueRef/excludedPoolRef (refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersExpanded, invRev, excludeExts, excludeDirs]);

  if (!open) return null;

  // Cola visible = lo aún no tomado por los workers (slice barato de punteros).
  const queue = queueHeadRef.current > 0 ? queueRef.current.slice(queueHeadRef.current) : queueRef.current;
  const succeeded = succeededRef.current;
  const failed = failedRef.current;
  const skipped = skippedRef.current;
  const activeWork = Array.from(activeWorkRef.current.entries());
  const noFiles = queue.length === 0 && succeeded.length === 0 && failed.length === 0;
  const canStart = queue.length > 0 && !!targetFolderRef && !processing && online;
  const currentConcurrency = concurrencyRef.current?.getCurrent() || 3;

  const totalProcessed = succeeded.length + failed.length;
  const totalAll = totalProcessed + queue.length + activeWork.length;
  const pct = totalAll > 0 ? Math.round((totalProcessed / totalAll) * 100) : 0;
  const showProgress = processing || totalProcessed > 0 || queue.length > 0;

  const queueVisible    = queue.slice(0, VISIBLE_LIMIT);
  const succeededVisible = succeeded.slice(-VISIBLE_LIMIT).reverse();
  const failedVisible   = failed.slice(-VISIBLE_LIMIT).reverse();
  const skippedVisible  = skipped.slice(-VISIBLE_LIMIT).reverse();

  const excludedPool = excludedPoolRef.current;

  return (
    <div className="bu-overlay daysi-vista theme-light" onClick={handleClose}>
      <section className="bu-modal" onClick={e => e.stopPropagation()}>

        <header className="bu-head">
          <div className="bu-head-left">
            <div className="bu-head-icon">{Ico.cloud()}</div>
            <div className="bu-head-title">
              <span className="bu-head-label">Carga masiva de archivos</span>
              <div className="bu-head-name" title={cleanFolderName}>
                <span className="bu-head-arrow">→</span> {cleanFolderName || <em className="muted">Sin destino seleccionado</em>}
              </div>
            </div>
          </div>
          <button className="bu-icon-btn" onClick={handleClose} title={processing ? 'Cancelar y cerrar' : 'Cerrar (Esc)'}>
            {Ico.close()}
          </button>
        </header>

        {/* Banner de recovery */}
        {recoveryCandidates.length > 0 && !processing && noFiles && (
          <div className="bu-recovery-banner">
            <span className="bu-recovery-icon">{Ico.warn()}</span>
            <div className="bu-recovery-body">
              <div className="bu-recovery-title">
                {recoveryCandidates.length === 1
                  ? 'Hay una carga anterior incompleta'
                  : `Hay ${recoveryCandidates.length} cargas anteriores incompletas`}
              </div>
              {recoveryCandidates.slice(0, 3).map(c => (
                <div key={c.batchId} className="bu-recovery-item">
                  <span className="bu-recovery-info">
                    {c.folderName || c.batchId.substring(0, 14)} ·
                    ✓ {c.succeededCount || 0} · ✗ {c.failedCount || 0} · ⌛ {c.queueLength || 0}
                  </span>
                  <div className="bu-recovery-actions">
                    <button className="bu-btn-primary sm" onClick={() => resumeBatch(c.batchId)}>Reanudar</button>
                    <button className="bu-btn-ghost sm" onClick={() => discardRecovery(c.batchId)}>Descartar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Banner offline */}
        {!online && (
          <div className="bu-net-banner">
            <span className="bu-net-icon">{Ico.offline()}</span>
            <span>Sin conexión a internet. Los archivos no se podrán subir hasta que la red vuelva.</span>
          </div>
        )}

        {/* Banner de circuit breaker abierto */}
        {breakerState === 'open' && (
          <div className="bu-circuit-banner">
            <span className="bu-circuit-icon">{Ico.warn()}</span>
            <span>Pausando temporalmente: el servidor está reportando errores. Reanudando en {Math.ceil((breakerRef.current?.cooldownRemainingMs() || 0) / 1000)}s.</span>
          </div>
        )}

        {/* Selectores */}
        <div className="bu-pickers">
          <input ref={filePickerRef} type="file" multiple style={{ display: 'none' }} onChange={onPickFiles} />
          <input
            ref={folderPickerRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            // eslint-disable-next-line react/no-unknown-property
            webkitdirectory=""
            // eslint-disable-next-line react/no-unknown-property
            directory=""
            onChange={onPickFolder}
          />
          {!isDocTarget && (
            <button className="bu-btn-secondary" onClick={() => filePickerRef.current?.click()} disabled={processing}>
              {Ico.file()} <span>Seleccionar archivos</span>
            </button>
          )}
          <button className="bu-btn-secondary" onClick={onPickFolderButton} disabled={processing}>
            {Ico.folder()} <span>Seleccionar carpeta</span>
          </button>
          {isDocTarget && (
            <span style={{ fontSize: 11.5, opacity: .65, alignSelf: 'center' }}>
              En la raíz solo se pueden subir carpetas completas.
            </span>
          )}
        </div>

        {/* Modo árbol (contador, supervisora y root) */}
        {canUploadTree && (
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--line, #e8e8e8)' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: processing ? 'not-allowed' : 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={treeMode}
                disabled={processing || isDocTarget}
                onChange={(e) => { setTreeModeBoth(e.target.checked); if (!e.target.checked) setIncludeEmptyBoth(false); }}
                style={{ marginTop: 2 }}
              />
              <span>
                <strong>Subir también la estructura de árbol de la carpeta{isDocTarget ? ' (obligatorio en la raíz)' : ''}</strong>
                <span style={{ display: 'block', fontSize: 11.5, opacity: .7, marginTop: 2, lineHeight: 1.4 }}>
                  Se replicará la carpeta arrastrada y sus subcarpetas en el destino, y cada archivo se
                  subirá a su subcarpeta correspondiente. Si ya existe una subcarpeta con el mismo nombre,
                  se fusionará con ella.
                </span>
              </span>
            </label>
            {/* Depende de la estructura: con sangría y deshabilitada sin ella. */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8, marginLeft: 26, paddingLeft: 12, borderLeft: '2px solid var(--line, #f0f0f0)', cursor: (processing || !treeMode) ? 'not-allowed' : 'pointer', fontSize: 13, opacity: treeMode ? 1 : 0.45 }}>
              <input
                type="checkbox"
                checked={includeEmpty}
                disabled={processing || !treeMode}
                onChange={(e) => setIncludeEmptyBoth(e.target.checked)}
                style={{ marginTop: 2 }}
              />
              <span>
                Incluir las carpetas vacías
                <span style={{ display: 'block', fontSize: 11.5, opacity: .7, marginTop: 2, lineHeight: 1.4 }}>
                  Se crean también las subcarpetas que no traen archivos, salvo las que omitas con los
                  filtros. Para que se detecten, arrastra la carpeta al recuadro.
                </span>
              </span>
            </label>
          </div>
        )}

        {/* Filtros de exclusión (todos los usuarios) */}
        <div style={{ padding: '6px 16px', borderBottom: '1px solid var(--line, #e8e8e8)' }}>
          <button
            type="button"
            onClick={() => setFiltersExpanded(v => !v)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'inherit', width: '100%', textAlign: 'left' }}
            title={filtersExpanded ? 'Contraer' : 'Expandir'}
          >
            {Ico.chevron(filtersExpanded)}
            <span style={{ fontWeight: 600 }}>¿Quieres omitir algunos archivos?</span>
            {excludedPool.length > 0 ? (
              <span style={{
                fontSize: 10.5, background: 'rgba(231,8,16,.08)', border: '1px solid rgba(231,8,16,.25)',
                borderRadius: 10, padding: '0 8px'
              }}>
                {excludedPool.length.toLocaleString('es-CO')} apartado(s)
              </span>
            ) : (
              <span style={{ fontSize: 11, opacity: .55 }}>Opcional</span>
            )}
          </button>
          {filtersExpanded && (
            <div style={{ marginTop: 8 }}>
              {queue.length + excludedPool.length === 0 ? (
                <div style={{ fontSize: 11.5, opacity: .6, padding: '4px 0 6px' }}>
                  Agrega archivos y aquí verás los tipos de archivo y las carpetas detectadas,
                  para quitar con un clic lo que no quieras subir.
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  {/* Columna: tipos de archivo detectados (virtualizada) */}
                  <div style={{ flex: '1 1 220px', minWidth: 200 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, opacity: .8, marginBottom: 4 }}>
                      📄 Tipos de archivo detectados {extRows.length > 0 && <span style={{ fontWeight: 400, opacity: .6 }}>({extRows.length.toLocaleString('es-CO')})</span>}
                    </div>
                    <VirtualRows
                      rows={extRows}
                      emptyText="Sin extensiones reconocibles."
                      renderRow={r => (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px', height: '100%',
                          fontSize: 12, borderBottom: '1px solid var(--line, #f0f0f0)', opacity: r.excluded ? .55 : 1
                        }}>
                          <span style={{ textDecoration: r.excluded ? 'line-through' : 'none', fontWeight: 500 }}>.{r.tok}</span>
                          <span style={{ opacity: .6, fontSize: 11 }}>{r.count.toLocaleString('es-CO')} archivo(s)</span>
                          <button
                            type="button"
                            disabled={processing}
                            onClick={() => r.excluded ? removeExcludeExt(r.tok) : addExcludeExt(r.tok)}
                            title={r.excluded ? 'Restaurar estos archivos a la carga' : 'No subir estos archivos'}
                            style={inventoryBtnStyle}
                          >{r.excluded ? '↩' : '×'}</button>
                        </div>
                      )}
                    />
                  </div>
                  {/* Columna: árbol de carpetas detectado (virtualizada) */}
                  <div style={{ flex: '1 1 280px', minWidth: 230 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, opacity: .8, marginBottom: 4 }}>
                      📁 Carpetas detectadas {dirRows.length > 0 && <span style={{ fontWeight: 400, opacity: .6 }}>({dirRows.length.toLocaleString('es-CO')})</span>}
                    </div>
                    <VirtualRows
                      rows={dirRows}
                      emptyText="Los archivos agregados no traen carpetas."
                      renderRow={r => (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 6, height: '100%',
                          padding: `0 10px 0 ${10 + Math.min(r.depth, 12) * 14}px`,
                          fontSize: 12, borderBottom: '1px solid var(--line, #f0f0f0)', opacity: r.excluded ? .55 : 1
                        }}>
                          <span style={{ flexShrink: 0 }}>📁</span>
                          <span
                            title={r.key}
                            style={{
                              textDecoration: r.excluded ? 'line-through' : 'none',
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}
                          >{r.name}</span>
                          <span style={{ opacity: .6, fontSize: 11, flexShrink: 0 }}>({r.count.toLocaleString('es-CO')})</span>
                          {r.excluded && !r.direct ? (
                            <span style={{ marginLeft: 'auto', fontSize: 10.5, opacity: .5, flexShrink: 0 }} title="Apartada por una carpeta superior">—</span>
                          ) : (
                            <button
                              type="button"
                              disabled={processing}
                              onClick={() => r.direct ? removeExcludeDir(r.key) : addExcludeDir(r.key)}
                              title={r.direct ? 'Restaurar esta carpeta a la carga' : 'No subir esta carpeta ni su contenido'}
                              style={inventoryBtnStyle}
                            >{r.direct ? '↩' : '×'}</button>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              )}
              {excludedPool.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 11.5, opacity: .7 }}>
                  {excludedPool.length.toLocaleString('es-CO')} archivo(s) apartado(s) — no se subirán. Usa ↩ para restaurarlos.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Drop zone */}
        <div ref={dropRef} className={`bu-drop ${noFiles ? 'large' : 'compact'}`}>
          {noFiles && <div className="bu-drop-cloud">{Ico.cloud()}</div>}
          <div className="bu-drop-text">
            {noFiles ? <>Arrastra <strong>archivos o carpetas</strong> aquí</> : <>+ Arrastra aquí para añadir más</>}
          </div>
          {noFiles && (
            <div className="bu-drop-hint">
              {treeMode
                ? <>La estructura de subcarpetas <em>se replicará</em> en el destino.</>
                : <>La estructura de subcarpetas <em>no</em> se replica.{canUploadTree && <> Activa la opción de árbol para conservarla.</>}</>}
              <br />
              <span className="bu-drop-hint-sm">
                {unlimited
                  ? 'Sin límite de cantidad ni tamaño para tu usuario.'
                  : `Hasta ${DEFAULT_LIMITS.maxFilesPerBatch.toLocaleString('es-CO')} archivos · max ${(DEFAULT_LIMITS.maxSizePerFile / 1024 / 1024).toFixed(0)} MB c/u`}
              </span>
            </div>
          )}
        </div>

        {/* Columnas */}
        <div className="bu-columns">
          <div className="bu-col">
            <div className="bu-col-head">
              <span className="bu-col-head-l"><span className="bu-col-head-ico">{Ico.queue()}</span><span>En cola</span></span>
              <span className="bu-badge">{queue.length.toLocaleString('es-CO')}</span>
            </div>
            <div className="bu-col-body">
              {queue.length === 0 && <div className="bu-empty">Sin archivos en cola.</div>}
              {queueVisible.map(item => (
                <FileRow key={item.id} item={item}
                  action={!processing && (<button className="bu-icon-btn danger" onClick={() => removeFromQueue(item.id)} title="Quitar de la cola">{Ico.trash()}</button>)}
                />
              ))}
              {queue.length > VISIBLE_LIMIT && (
                <div className="bu-virtual-hint">+ {(queue.length - VISIBLE_LIMIT).toLocaleString('es-CO')} archivo(s) más en cola</div>
              )}
            </div>
          </div>

          <div className="bu-col">
            <div className="bu-col-head">
              <span className="bu-col-head-l"><span className="bu-col-head-ico bu-col-head-ok">{Ico.success()}</span><span>Exitosos</span></span>
              <div className="bu-col-head-right">
                <span className="bu-badge bu-badge-ok">{succeeded.length.toLocaleString('es-CO')}</span>
                {succeeded.length > 0 && !processing && (
                  <button className="bu-icon-btn" onClick={clearSucceeded} title="Limpiar lista">{Ico.trash()}</button>
                )}
              </div>
            </div>
            <div className="bu-col-body">
              {succeeded.length === 0 && <div className="bu-empty">Ningún archivo subido aún.</div>}
              {succeededVisible.map(item => (
                <FileRow key={item.id} item={item} action={<span className="bu-row-ok-pill">{Ico.check()}</span>} />
              ))}
              {succeeded.length > VISIBLE_LIMIT && (
                <div className="bu-virtual-hint">+ {(succeeded.length - VISIBLE_LIMIT).toLocaleString('es-CO')} subido(s) anteriormente</div>
              )}
            </div>
          </div>

          <div className="bu-col">
            <div className="bu-col-head">
              <span className="bu-col-head-l"><span className="bu-col-head-ico bu-col-head-err">{Ico.fail()}</span><span>Fallidos</span></span>
              <span className="bu-badge bu-badge-err">{failed.length.toLocaleString('es-CO')}</span>
            </div>
            <div className="bu-col-body">
              {failed.length === 0 && <div className="bu-empty">Sin fallos.</div>}
              {failedVisible.map(item => (
                <FileRow key={item.id} item={item}
                  action={
                    <div className="bu-row-actions">
                      <button className="bu-icon-btn" onClick={() => retryOne(item.id)} title="Reintentar">{Ico.retry()}</button>
                      <button className="bu-icon-btn danger" onClick={() => removeFromFailed(item.id)} title="Descartar">{Ico.trash()}</button>
                    </div>
                  }
                />
              ))}
              {failed.length > VISIBLE_LIMIT && (
                <div className="bu-virtual-hint">+ {(failed.length - VISIBLE_LIMIT).toLocaleString('es-CO')} fallo(s) anteriores</div>
              )}
            </div>
          </div>

          {skipped.length > 0 && (
            <div className="bu-col">
              <div className="bu-col-head">
                <span className="bu-col-head-l"><span className="bu-col-head-ico">{Ico.queue()}</span><span>Omitidos</span></span>
                <span className="bu-badge">{skipped.length.toLocaleString('es-CO')}</span>
              </div>
              <div className="bu-col-body">
                {skippedVisible.map(item => (
                  <FileRow key={item.id} item={item}
                    action={<span className="bu-row-skip-pill" title="Ya existía en el destino; no se reemplazó">⊘</span>}
                  />
                ))}
                {skipped.length > VISIBLE_LIMIT && (
                  <div className="bu-virtual-hint">+ {(skipped.length - VISIBLE_LIMIT).toLocaleString('es-CO')} omitido(s) más</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Actividad + progreso: panel inferior unificado. La barra vive aquí
            (pegada al footer) y el detalle completo sigue en el dropdown. */}
        <div className={`bu-log ${logExpanded ? 'expanded' : 'collapsed'}`}>
          {showProgress && (
            <div className="bu-progress-bar" style={{ height: 4, borderRadius: 0 }} title={`${pct}% — ${totalProcessed.toLocaleString('es-CO')} de ${totalAll.toLocaleString('es-CO')} procesados`}>
              <div className="bu-progress-fill" style={{ width: `${pct}%` }} />
            </div>
          )}
          <button type="button" className="bu-log-head bu-log-toggle"
            onClick={() => setLogExpanded(v => !v)} title={logExpanded ? 'Contraer detalle' : 'Ver detalle de actividad'}>
            <span className="bu-log-head-l">
              <span className="bu-log-chevron">{Ico.chevron(logExpanded)}</span>
              <span>Actividad</span>
              {activeWork.length > 0 ? (
                <span className="bu-log-last" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span className="bu-progress-spinner" />
                  <span title={activeWork.map(([, n]) => n).join('\n')}>
                    Subiendo {activeWork.length === 1 ? activeWork[0][1] : `${activeWork[0][1]} +${activeWork.length - 1}`}
                  </span>
                </span>
              ) : !logExpanded && log.length > 0 ? (
                <span className={`bu-log-last bu-log-last-${log[log.length - 1].level}`}>
                  {log[log.length - 1].msg}
                </span>
              ) : null}
            </span>
            <span className="muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
              {showProgress && (
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <strong>{totalProcessed.toLocaleString('es-CO')}</strong> / {totalAll.toLocaleString('es-CO')}
                  {' · '}<span style={{ color: 'var(--accent)', fontWeight: 700 }}>{pct}%</span>
                  {processing && ` · ⚡ ${activeWork.length}/${currentConcurrency}`}
                </span>
              )}
              <span>{log.length.toLocaleString('es-CO')} {log.length === 1 ? 'evento' : 'eventos'}</span>
            </span>
          </button>
          {logExpanded && (
            <div className="bu-log-body" ref={logBodyRef}>
              {log.length === 0 && <div className="bu-empty bu-empty-log">El registro aparecerá aquí cuando inicies la subida.</div>}
              {log.slice().reverse().map((l, i) => (
                <div key={i} className={`bu-log-row bu-log-${l.level}`}>
                  <span className="bu-log-ts">{l.ts}</span>
                  <span className="bu-log-msg">{l.msg}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="bu-foot">
          <div className="bu-foot-left">
            {queue.length > 0 && !processing && (<button className="bu-btn-ghost" onClick={clearQueue}>Limpiar cola</button>)}
            {failed.length > 0 && !processing && (
              <button className="bu-btn-ghost" onClick={() => processQueue(true)}>
                {Ico.retry()} <span>Reintentar fallidos ({failed.length.toLocaleString('es-CO')})</span>
              </button>
            )}
          </div>
          <div className="bu-foot-right">
            {!processing && (
              <button className="bu-btn-primary lg" onClick={() => processQueue(false)} disabled={!canStart}
                title={!online ? 'Sin conexión a internet' : !targetFolderRef ? 'Sin carpeta destino' : (queue.length === 0 ? 'No hay archivos en cola' : '')}>
                {Ico.play()} <span>Iniciar carga</span>
              </button>
            )}
            {processing && (
              <button className="bu-btn-secondary" onClick={togglePause}>
                {paused ? Ico.play() : Ico.pause()}
                <span>{paused ? 'Reanudar' : 'Pausar'}</span>
              </button>
            )}
            {processing && (<button className="bu-btn-danger" onClick={cancelAll}>Cancelar todo</button>)}
          </div>
        </footer>

        {/* Diálogo de confirmación estilizado (reemplaza window.confirm) */}
        {confirmDlg && (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 10000,
              background: 'rgba(15, 18, 25, .45)', backdropFilter: 'blur(2px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              background: '#fff', borderRadius: 12, width: 440, maxWidth: '92%',
              boxShadow: '0 16px 48px rgba(0,0,0,.28)', overflow: 'hidden',
              animation: 'buDlgIn .18s cubic-bezier(0.2, 0.8, 0.2, 1)',
            }}>
              <div style={{ display: 'flex', gap: 12, padding: '18px 20px 4px' }}>
                <div style={{
                  flexShrink: 0, width: 38, height: 38, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: confirmDlg.danger ? 'rgba(250,173,20,.14)' : 'rgba(24,144,255,.12)',
                  color: confirmDlg.danger ? '#FAAD14' : '#1890ff',
                }}>
                  {Ico.warn()}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{confirmDlg.title}</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line', color: 'rgba(0,0,0,.65)' }}>
                    {confirmDlg.message}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '16px 20px 18px' }}>
                {confirmDlg.buttons ? (
                  confirmDlg.buttons.map(b => (
                    <button
                      key={b.key}
                      className={b.danger ? 'bu-btn-danger' : (b.primary ? 'bu-btn-primary' : 'bu-btn-ghost')}
                      onClick={() => settleConfirm(b.key)}
                    >
                      {b.label}
                    </button>
                  ))
                ) : (
                  <>
                    <button className="bu-btn-ghost" onClick={() => settleConfirm(false)}>
                      {confirmDlg.cancelText || 'Cancelar'}
                    </button>
                    <button
                      className={confirmDlg.danger ? 'bu-btn-danger' : 'bu-btn-primary'}
                      onClick={() => settleConfirm(true)}
                    >
                      {confirmDlg.okText || 'Aceptar'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
